import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FilePlus2, Loader2 } from "lucide-react";
import { todayISO } from "@/lib/dfr";

// Start a brand-new Daily Field Report on a chosen date (one report per day per user).
export const NewReportDialog = ({ open, onOpenChange, onOpen }) => {
  const [date, setDate] = useState(todayISO());
  const [existing, setExisting] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setDate(todayISO()); setExisting(null); } }, [open]);

  const create = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    setBusy(true);
    try {
      const list = (await axios.get(`/field-reports?date=${date}`)).data || [];
      if (list[0]) { setExisting(list[0]); return; }
      await axios.post("/field-reports", { date, fielder: "", rows: [], track: [] });
      toast.success(`New report started for ${date}`);
      onOpen(date);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the report"); }
    finally { setBusy(false); }
  };

  const startFresh = async () => {
    if (!window.confirm(`Clear all ${existing.rows?.length || 0} row(s) on the ${date} report and start from scratch?`)) return;
    setBusy(true);
    try {
      await axios.put(`/field-reports/${existing.id}`, { date, fielder: "", rows: [], track: [] });
      toast.success(`Report for ${date} reset`);
      onOpen(date, true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not reset the report"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm bg-white z-[1300]" aria-describedby={undefined} data-testid="dfr-new-report-dialog">
        <DialogHeader><DialogTitle className="text-sm font-mono uppercase flex items-center gap-2"><FilePlus2 className="w-4 h-4" />Create New Daily Report</DialogTitle></DialogHeader>
        <label className="text-[10px] font-mono uppercase text-zinc-500">Report date</label>
        <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); setExisting(null); }} className="h-9 bg-zinc-50" data-testid="dfr-new-report-date" />
        {existing ? (
          <div className="border border-amber-300 bg-amber-50 rounded p-2 space-y-2" data-testid="dfr-new-report-exists">
            <p className="text-xs text-amber-900">A report for <b>{date}</b> already exists ({existing.rows?.length || 0} job row{existing.rows?.length === 1 ? "" : "s"}).</p>
            <div className="flex gap-2">
              <Button type="button" disabled={busy} onClick={() => onOpen(date)} className="h-8 text-xs bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="dfr-new-report-open-existing">Open it</Button>
              <Button type="button" disabled={busy} onClick={startFresh} variant="outline" className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid="dfr-new-report-start-fresh">Start fresh (clear rows)</Button>
            </div>
          </div>
        ) : (
          <Button type="button" disabled={busy || !date} onClick={create} className="h-9 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="dfr-new-report-create">
            {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FilePlus2 className="w-4 h-4 mr-1" />}Create report
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewReportDialog;
